import { Autocomplete, Box, createFilterOptions } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useMyTeamStore } from 'src/feature/my-team/store/my-team.store'
import { useNotificationStore } from 'src/feature/notification/store/notification.store'
import { ProfessionFieldsType } from 'src/feature/skill-assessment/model/skill-assessment-model'
import { useSkillAssessmentStore } from 'src/feature/skill-assessment/store/skill-assessment.store'
import { generateUniqId } from 'src/model/Id'

export type ExpectedRatingProps = {
  levelId: string
  disablePaper?: boolean
  onChange: () => void
}

const ROLE_NAME_MAX_LENGTH = 50
const PROFESSION_FIELD_MAX_LENGTH = 150

export const AddExpectedRole = observer(({ levelId, disablePaper = false, onChange }: ExpectedRatingProps) => {
  const { translation } = useLocalizationStore()
  const myTeamStore = useMyTeamStore()
  const skillAssessmentStore = useSkillAssessmentStore()
  const notificationStore = useNotificationStore()
  
  const [professionField, setProfessionField] = useState<ProfessionFieldsType | null>(null)
  const filter = createFilterOptions<ProfessionFieldsType>()
  const [roleName, setRoleName] = useState<string>(myTeamStore.teamRolesOrganisationOverview?.roles?.find(level => level.id === levelId)?.name || 'NA')
  const translations = translation.pages.myTeam

  useEffect(() => {
    !skillAssessmentStore.professionsFieldsData?.length && skillAssessmentStore.getProfessionFields()
  }, [levelId])

  const handleProfessionFieldsChange = (_: any, newValue: ProfessionFieldsType | null) => {
    if (newValue && newValue.inputValue) {
      setProfessionField({
        id: newValue.id,
        title: newValue.inputValue,
      })
    } else {
      setProfessionField(newValue || null)
    }
  }

  const handleProfessionFieldsFilterChange = (options: Array<ProfessionFieldsType>, params: any) => {
    const filtered = filter(options, params)
    const { inputValue } = params
    const isExisting = options.some(option => inputValue === option.title)
    
    if (inputValue !== '' && !isExisting) {
      filtered.push({
        inputValue,
        id: generateUniqId(),
        title: `Add '${inputValue}'`,
      })
    }

    return filtered
  }

  const handleProfessionFieldsSave = async () => {
    const roleNameRegex = /^.{1,50}$/
    const professionFieldRegex = /^.{1,150}$/
    
    if (!professionField || !professionFieldRegex.test(professionField.title) || !roleName || !roleNameRegex.test(roleName)) {
      notificationStore.enqueueError(translations.errorMsg.roleError())
      
      return
    }

    try {
      await skillAssessmentStore.addSkillCategory(professionField.title, roleName, levelId)
      notificationStore.enqueueSuccess(translations.errorMsg.roleAdded())
      onChange()
    } catch {
      notificationStore.enqueueError(translation.util.unknownErrorOccurred())
    }
  }

  return (
    <Box sx={styles.noSkillWrapper}>
      <AppBox sx={styles.professionSelect}>
        <AppTextField
          defaultValue={roleName}
          placeholder={translations.setDemandRolePlaceholder()}
          sx={{
            fontSize: 12,
            m: 0,
            width: '30%',
            flexShrink: 0,
            '& .MuiInputBase-formControl': {
              backgroundColor: disablePaper ? 'background.paper' : 'background.dark',
              height: 38,
            },
          }}
          onChange={(e: any) => setRoleName(e.target.value)}
          inputProps={{ maxLength: ROLE_NAME_MAX_LENGTH }}
        />
        <Autocomplete
          sx={{
            width: '70%',
            '& .MuiInputBase-formControl': {
              backgroundColor: disablePaper ? 'background.paper' : 'background.dark',
              height: 38,
            },
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
              color: 'text.primary',
            },
            '& .MuiChip-label': {
              fontSize: 12,
            },
          }}
          value={professionField}
          onChange={handleProfessionFieldsChange}
          filterOptions={handleProfessionFieldsFilterChange}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          id='professionField'
          size='small'
          options={skillAssessmentStore.professionsFieldsData || []}
          getOptionLabel={option => option.title || ''}
          isOptionEqualToValue={(option, value) => {
            // Ensure both option and value are defined and compare their IDs
            return option?.id === value?.id
          }}
          renderOption={(props, option) => (
            <li {...props}>
              {option.title}
            </li>
          )}
          renderInput={params => (
            <AppTextField
              {...params}
              placeholder={translations.setDemandFieldPlaceholder()}
              sx={{
                fontSize: 12,
                m: 0,
              }}
              inputProps={{
                ...params.inputProps,
                maxLength: PROFESSION_FIELD_MAX_LENGTH,
              }}
            />
          )}
        />
      </AppBox>
      <AppButton
        sx={{ width: '10em', fontSize: 12, alignSelf: 'center' }}
        variant='contained'
        onClick={handleProfessionFieldsSave}
        disabled={!professionField || !roleName}
      >
        {translations.setDemandButton()}
      </AppButton>
    </Box>
  )
})

const styles = {
  noSkillWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  professionSelect: {
    flexDirection: { xs: 'column', md: 'row' },
    gap: 10,
    alignItems: 'center',
    justifyContent: 'center',
    width: { xs: '100%', md: '50%' },
  },
}
